import { put, select, takeLatest } from 'redux-saga/effects';
import { RootState } from '../..';
import { GLOBAL_URL } from '../../../shared/constants/dataServiceUrl';
import { Payer } from '../../../shared/models/payer.model';
import ApplicationDetail from '../../../shared/services/applicationDetail.service';
import SendRequest from '../../../shared/services/sendRequest.service';
import { setCoverageResponse, setIPOResponse } from '../../actions/nhcxAction';
import { NHCXState } from '../../models/nhcxModel';
import actionTypes from '../../types/nhcxTypes';
import { COMMON_ERROR, PARTICIPANT_ID_EXISTING } from '../../../shared/constants/commonConstants';
import { centeredNotification } from '../../../utils/utility';
import { baseUrl } from '../../../shared/constants/crmConstants';

export function* fetchPolicyDetails({ payload, onSuccess, onError }: any): any {
  const sendRequest = SendRequest.instance;
  try {
    const url = `${GLOBAL_URL.CRM_BASE_URL}Provider/Search/NHCXPolicySearch`;
    const policyDetails = yield sendRequest.postBenef<any, any>(url, payload, { isShowErrorInInterceptor: false });
    const { memberDetails, isSuccess } = policyDetails;
    if (isSuccess) {
      onSuccess(memberDetails);
    } else {
      onError();
    }
  } catch (err: any) {
    onError();
  }
}

export function* fetchIPODetails({ payload, onSuccess, onError }: any): any {
  const sendRequest = SendRequest.instance;
  payload = { ...payload, NHCXEnabledRoutingAPI: 'IPO' };
  try {
    const url = `${GLOBAL_URL.CRM_BASE_URL}Provider/Search/NHCXIPOSearch`;
    const ipoDetails = yield sendRequest.postBenef<any, any>(url, payload, { isShowErrorInInterceptor: false });
    const { memberDetails, isSuccess} = ipoDetails;
    if (isSuccess) {
      onSuccess(memberDetails[0]?.ipoCorelationId);
    } else {
      onError();
    }
  } catch (err: any) {
    onError();
  }
}
export function* fetchNHCXResponse(): any {
  const appDetails = ApplicationDetail.instance;

  const { id: payerId }: any = yield select((state: RootState): Payer => state?.payerList?.payer || {});
  const {selectedPolicyDetail}: any = yield select((state: RootState): NHCXState => state?.nhcx || {});

  const sendRequest = SendRequest.instance;
  const payload = {
    providerId: appDetails?.getProviderId()?.toString(),
    payerId: payerId?.toString(),
    memberId: selectedPolicyDetail?.memberId,
    productId: selectedPolicyDetail?.productCode,
  };
  try {
    const url = `${GLOBAL_URL.CRM_BASE_URL}Provider/GetNHCXResponse`;
    const {ipoSearch, isSuccess, memberSearch} = yield sendRequest.post<any, any>(url, payload, undefined, undefined, false);
    if (ipoSearch && isSuccess) {
     yield put(setIPOResponse(ipoSearch));
     yield put(setCoverageResponse(memberSearch[0]));

    } else {
    }
  } catch (err: any) {
  }
}

// NHCX Participant Creation
function* saveParticipantDetails({ payload: { data, onSuccess, onError, onNotifyAlreadyExisting }}: any ): Generator {
  try {
    const sendRequest = SendRequest.instance;
    const url =  GLOBAL_URL.CRM_BASE_URL + 'NHCX/ParticipationCreation';
    const response  = yield sendRequest?.post(url, data);
    const { isSuccess, errorMessage = [], transactionid, message, participantid }: any = response;
    if (isSuccess) {
      if (message?.toUpperCase() === PARTICIPANT_ID_EXISTING) {
        onNotifyAlreadyExisting(message, participantid);  // if participant already existing preventing otp flow
      } else {
        onSuccess(transactionid, message, participantid);
      }
    } else {
      onError(errorMessage);
    }
  } catch {
    centeredNotification(COMMON_ERROR);
  }
}

// NHCX Participant Creation Confirmation
function* confirmParticipantDetails({ payload: { data, onSuccess, onError }}: any ): Generator {
  try {
    const sendRequest = SendRequest.instance;
    const url =  GLOBAL_URL.CRM_BASE_URL + 'NHCX/ConfirmParticipationCreation';
    const response  = yield sendRequest?.post(url, data);
    const { isSuccess, errorMessage = [], message }: any = response;
    if (isSuccess) {
      onSuccess(message);
    } else {
      onError(errorMessage);
    }
  } catch {
    centeredNotification(COMMON_ERROR);
  }
}

// Get Participant Details
function* fetchParticipantDetails({ payload: { providerId, onSuccess, onError } }: any): Generator {
  const isVersioningPostAPIEnabled = ApplicationDetail?.instance?.getIsVersioningPostAPIEnabled();
  try {
    const sendRequest = SendRequest.instance;
    let response: any;
    const payload = {
      providerId: providerId?.toString(),
    };
    const url = `${baseUrl}NHCX/FetchParticipationData`;
    if (isVersioningPostAPIEnabled) {
      response = yield sendRequest.post(url, payload, undefined, undefined, undefined, undefined, true);
    } else {
      response = yield sendRequest.get(`${url}?providerId=${providerId}`);
    }
    const { isSuccess, errorMessage = [] }: any = response;
    if (isSuccess) {
      onSuccess(response);
    } else {
      onError(errorMessage);
    }
  } catch (e) {
    centeredNotification(COMMON_ERROR);
  }
}

// Get facility regsitry data
function* fetchRegistryData({ payload: { facilityId, onSuccess, onError } }: any): Generator {
  const isVersioningPostAPIEnabled = ApplicationDetail?.instance?.getIsVersioningPostAPIEnabled();
  try {
    const sendRequest = SendRequest.instance;
    let response: any;
    const url = `${baseUrl}/NHCX/FetchFacilityData`;
    if (isVersioningPostAPIEnabled) {
      // If IsVersioningPostAPIEnabled is true, perform a POST request
      response = yield sendRequest.post(url, facilityId);
    } else {
      // If IsVersioningPostAPIEnabled is false, perform a GET request
      response = yield sendRequest.get(`${url}?facilityId=${facilityId}`);
    }
    const { isSuccess, errorMessage = [] }: any = response;
    if (isSuccess) {
      onSuccess(response);
    } else {
      onError(errorMessage);
    }
  } catch (e) {
    centeredNotification(COMMON_ERROR);
  }
}

// UPDATE CERTIFICATES
function* updateCertificates({payload, onSuccess}: any): Generator {
  try {
    const sendRequest = SendRequest.instance;
    const url =  `${GLOBAL_URL.CRM_BASE_URL}/NHCX/UpdateParticipationCertificate`;
    const response  = yield sendRequest.post(url, payload);
    const { isSuccess }: any = response;
    if (isSuccess) {
      onSuccess();
    }
  } catch (err: any) {
  }
}
// Rollout payers for NHCX post onboarding of providers
function* rollOutPayersForNHCX(payload?: any): Generator {
  try {
    const sendRequest = SendRequest.instance;
    // tslint:disable-next-line: no-shadowed-variable
    const payload = {
    };
    const url = `${GLOBAL_URL.CRM_BASE_URL}/Provider/OnboardNHCXPayer`;
    const response = yield sendRequest.post(url, payload);
    const { isSuccess }: any = response;
    if (isSuccess) {
      return;
    }
  } catch (err: any) {
  }
}

export function* watchNhcxSaga(): any {
  yield takeLatest(actionTypes.GET_POLICY_SEARCH_RESULTS, fetchPolicyDetails);
  yield takeLatest(actionTypes.GET_IPO_DETAILS, fetchIPODetails);
  yield takeLatest(actionTypes.GET_NHCX_RESPONSE , fetchNHCXResponse);
  yield takeLatest(actionTypes.SAVE_PARTICIPANT , saveParticipantDetails);
  yield takeLatest(actionTypes.CONFIRM_PARTICIPANT , confirmParticipantDetails);
  yield takeLatest(actionTypes.GET_PARTICIPANT_DETAILS , fetchParticipantDetails);
  yield takeLatest(actionTypes.FETCH_FACILITY_REGISTRY , fetchRegistryData);
  yield takeLatest(actionTypes.UPDATE_CERTIFICATES , updateCertificates);
  yield takeLatest(actionTypes.ROLLOUT_PAYERS_NHCX , rollOutPayersForNHCX);
}

export default watchNhcxSaga;
